(() => {
  'use strict';
  if (window.PBDesignSnapshot) return;
  const VERSION = '2026-10-15.1';
  const strings = window.PBDesignSnapshotStrings || {};
  const assets = new Map();
  let busy = false;
  let fontRulesPromise;
  document.addEventListener('error', event => {
    const img = event.target;
    if (img instanceof HTMLImageElement && img.matches('[data-pb-design-image]') && img.dataset.pbDesignFallback) {
      const fallback = img.dataset.pbDesignFallback;
      delete img.dataset.pbDesignFallback;
      img.src = fallback;
    }
  }, true);
  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
  const frames = () => new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  const visible = el => !!el && el.getBoundingClientRect().width > 0 && el.getBoundingClientRect().height > 0 && getComputedStyle(el).display !== 'none' && getComputedStyle(el).visibility !== 'hidden';
  const fail = () => new Error(strings.failed || 'Your design preview could not be saved. Please try Add to cart again.');
  const limited = (promise, ms = 15000) => {
    let timer;
    return Promise.race([promise, new Promise((_, reject) => { timer = setTimeout(() => reject(fail()), ms); })]).finally(() => clearTimeout(timer));
  };
  const dataURL = blob => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  async function inlineURL(value, base = document.baseURI) {
    if (!value || value.startsWith('data:') || value.startsWith('#')) return value;
    const url = new URL(value, base).href;
    if (!assets.has(url)) {
      assets.set(url, fetch(url, { credentials: 'omit', cache: 'force-cache', signal: AbortSignal.timeout(30000) })
        .then(response => { if (!response.ok) throw fail(); return response.blob(); })
        .then(dataURL).catch(error => { assets.delete(url); throw error; }));
    }
    return assets.get(url);
  }
  async function inlineCSS(css, base) {
    const matches = [...css.matchAll(/url\(\s*(['"]?)(.*?)\1\s*\)/g)];
    for (const match of matches) {
      const value = await inlineURL(match[2], base);
      css = css.replace(match[0], `url("${value}")`);
    }
    return css;
  }
  function fontRules() {
    if (!fontRulesPromise) fontRulesPromise = (async () => {
      const rules = [];
      const seen = new Set();
      const visit = async sheet => {
        if (seen.has(sheet.href || sheet)) return;
        seen.add(sheet.href || sheet);
        let list;
        try { list = [...sheet.cssRules]; }
        catch {
          if (!sheet.href) return;
          const response = await fetch(sheet.href, { credentials: 'omit', cache: 'force-cache', signal: AbortSignal.timeout(30000) });
          if (!response.ok) return;
          const css = await response.text();
          for (const match of css.matchAll(/@font-face\s*\{[^}]*\}/gi)) rules.push({ css: match[0], base: sheet.href });
          return;
        }
        for (const rule of list) {
          if (rule.type === CSSRule.FONT_FACE_RULE) rules.push({ css: rule.cssText, base: sheet.href || document.baseURI });
          if (rule.type === CSSRule.IMPORT_RULE && rule.styleSheet) await visit(rule.styleSheet);
          if (rule.cssRules) for (const child of rule.cssRules) if (child.type === CSSRule.FONT_FACE_RULE) rules.push({ css: child.cssText, base: sheet.href || document.baseURI });
        }
      };
      await Promise.all([...document.styleSheets].map(sheet => visit(sheet).catch(() => {})));
      return rules;
    })().catch(error => { fontRulesPromise = null; throw error; });
    return fontRulesPromise;
  }
  function styledClone(source, families, jobs, pseudoRules) {
    if (source.nodeType === Node.TEXT_NODE) return source.cloneNode();
    if (!(source instanceof Element) || ['SCRIPT', 'STYLE', 'LINK', 'IFRAME'].includes(source.tagName)) return null;
    const computed = getComputedStyle(source);
    if (computed.display === 'none') return null;
    let clone;
    if (source instanceof HTMLCanvasElement) {
      clone = document.createElement('img');
      clone.src = source.toDataURL('image/png');
    } else clone = source.cloneNode(false);
    for (const attr of [...clone.attributes]) if (/^on/i.test(attr.name) || ['srcset', 'sizes', 'loading', 'id'].includes(attr.name)) clone.removeAttribute(attr.name);
    clone.style.cssText = '';
    for (const prop of computed) {
      if (!prop.startsWith('--') && prop !== 'cursor') clone.style.setProperty(prop, computed.getPropertyValue(prop));
    }
    clone.style.setProperty('animation', 'none', 'important');
    clone.style.setProperty('transition', 'none', 'important');
    if ([...source.childNodes].some(child => child.nodeType === Node.TEXT_NODE && child.textContent.trim())) computed.fontFamily.split(',').forEach(f => families.add(f.trim().replace(/^['"]|['"]$/g, '').toLowerCase()));
    for (const pseudo of ['::before', '::after']) {
      const ps = getComputedStyle(source, pseudo);
      if (!ps.content || ['none', 'normal'].includes(ps.content) || ps.display === 'none') continue;
      const key = `pb-snap-${pseudoRules.length}`;
      clone.classList.add(key);
      let css = '';
      for (const prop of ps) {
        if (!prop.startsWith('--') && prop !== 'cursor') css += `${prop}:${ps.getPropertyValue(prop)};`;
      }
      pseudoRules.push(`.${key}${pseudo}{${css}}`);
      ps.fontFamily.split(',').forEach(f => families.add(f.trim().replace(/^['"]|['"]$/g, '').toLowerCase()));
    }
    if (source instanceof HTMLImageElement) {
      jobs.push(inlineURL(source.currentSrc || source.src).then(url => { clone.src = url; }));
    }
    if (source instanceof SVGImageElement) {
      jobs.push(inlineURL(source.href.baseVal).then(url => { clone.setAttribute('href', url); clone.removeAttribute('xlink:href'); }));
    }
    if (source instanceof HTMLInputElement) clone.setAttribute('value', source.value);
    if (source instanceof HTMLTextAreaElement) clone.textContent = source.value;
    if (clone.style.cssText.includes('url(')) jobs.push(inlineCSS(clone.style.cssText, document.baseURI).then(css => { clone.style.cssText = css; }));
    if (!(source instanceof HTMLCanvasElement) && !(source instanceof HTMLTextAreaElement)) for (const child of source.childNodes) {
      const copied = styledClone(child, families, jobs, pseudoRules);
      if (copied) clone.appendChild(copied);
    }
    return clone;
  }
  async function settle(node) {
    await limited(document.fonts.ready);
    for (const image of node.querySelectorAll('img')) {
      if (!visible(image)) continue;
      await limited(image.decode());
      if (!image.naturalWidth) throw fail();
    }
    await frames();
  }
  function canvasHasVisualContent(canvas) {
    const probe = document.createElement('canvas');
    probe.width = 48;
    probe.height = 48;
    const pctx = probe.getContext('2d', { willReadFrequently: true });
    pctx.drawImage(canvas, 0, 0, probe.width, probe.height);
    const pixels = pctx.getImageData(0, 0, probe.width, probe.height).data;
    let meaningful = 0;
    const total = pixels.length / 4;
    for (let i = 0; i < pixels.length; i += 4) {
      const alpha = pixels[i + 3];
      if (alpha < 16) continue;
      const distanceFromWhite = (255 - pixels[i]) + (255 - pixels[i + 1]) + (255 - pixels[i + 2]);
      if (distanceFromWhite > 30) meaningful++;
    }
    return meaningful >= Math.max(4, Math.ceil(total * 0.002));
  }

  function captureError(stage, cause) {
    const error = new Error(stage);
    error.pbStage = stage;
    if (cause) error.cause = cause;
    return error;
  }

  function captureRemaining(state) {
    if (state.signal.aborted) throw captureError('capture-cancelled');
    const remaining = state.deadline - performance.now();
    if (remaining <= 0) throw captureError('capture-budget-exhausted');
    return remaining;
  }

  async function rasterizeSVG(svg, width, height, method, state) {
    let objectURL;
    let image;
    let abortHandler;
    let finished = false;
    try {
      const remaining = captureRemaining(state);
      if (remaining <= 1050) throw captureError('capture-budget-exhausted');
      image = new Image();
      image.decoding = 'async';
      const loaded = new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(captureError(method + '-svg-load'));
      });
      // image.decode() is used for data-URL; ensure the separate load promise never rejects unhandled.
      loaded.catch(() => {});
      if (method === 'data-url') {
        // This was the successful renderer on all eight saved previews in orders 41067/41068.
        image.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
      } else {
        objectURL = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
        image.src = objectURL;
      }
      const cancelled = new Promise((_, reject) => {
        abortHandler = () => {
          image.onload = image.onerror = null;
          image.removeAttribute('src');
          reject(captureError('capture-cancelled'));
        };
        state.signal.addEventListener('abort', abortHandler, { once: true });
      });
      const loadBudget = Math.min(method === 'data-url' ? 6500 : 4000, Math.max(250, remaining - 800));
      state.currentStage = method + '-decoding';
      try {
        await limited(Promise.race([method === 'data-url' ? image.decode() : loaded, cancelled]), loadBudget);
      } catch (error) {
        throw captureError(method + '-svg-decode', error);
      }
      captureRemaining(state);
      await frames();
      await wait(80);
      await frames();
      captureRemaining(state);

      const scale = Math.min(3, 1600 / Math.max(width, height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(width * scale);
      canvas.height = Math.ceil(height * scale);
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) throw captureError(method + '-canvas-context');
      state.currentStage = method + '-canvas-draw';
      try {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      } catch (error) {
        throw captureError(method + '-canvas-draw', error);
      }
      captureRemaining(state);
      finished = true;
      return canvas;
    } finally {
      if (abortHandler) state.signal.removeEventListener('abort', abortHandler);
      if (image) {
        image.onload = image.onerror = null;
        // Best-effort cancellation of any timed-out SVG load; don't retain the decoded resource.
        if (!finished) image.removeAttribute('src');
      }
      if (objectURL) URL.revokeObjectURL(objectURL);
    }
  }

    async function captureOnce(node, state) {
    state.currentStage = 'preview-settle';
    captureRemaining(state);
    if (!visible(node)) throw fail();
    await settle(node);
    captureRemaining(state);
    const bounds = node.getBoundingClientRect();
    const width = node.offsetWidth || bounds.width;
    const height = node.offsetHeight || bounds.height;
    if (width < 20 || height < 20) throw fail();

    state.currentStage = 'resource-clone';
    const families = new Set(), jobs = [], pseudos = [];
    const clone = styledClone(node, families, jobs, pseudos);
    if (!clone) throw fail();

    clone.style.setProperty('margin', '0', 'important');
    clone.style.setProperty('transform', 'none', 'important');
    clone.style.setProperty('position', 'relative', 'important');
    clone.style.setProperty('inset', 'auto', 'important');
    clone.style.setProperty('width', `${width}px`, 'important');
    clone.style.setProperty('height', `${height}px`, 'important');
    clone.style.setProperty('box-sizing', 'border-box', 'important');
    clone.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');

    const resources = Promise.all(jobs);
    resources.catch(() => {});

    const allFonts = await fontRules();
    const usedFonts = allFonts.filter(rule => {
      const family = rule.css.match(/font-family\s*:\s*([^;}]*)/i)?.[1]?.trim().replace(/^['"]|['"]$/g, '').toLowerCase();
      return families.has(family);
    });

    const styles = await Promise.all(
      usedFonts.map(rule => inlineCSS(rule.css.replace(/local\([^)]*\)\s*,?/g, ''), rule.base))
    );
    captureRemaining(state);
    await resources;
    captureRemaining(state);

    const pseudoCSS = await inlineCSS(pseudos.join('\n'), document.baseURI);
    captureRemaining(state);
    const style = document.createElement('style');
    style.textContent = styles.join('\n') + '\n' + pseudoCSS;
    clone.prepend(style);

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><foreignObject x="0" y="0" width="100%" height="100%">${new XMLSerializer().serializeToString(clone)}</foreignObject></svg>`;
    const failed = [];
    // Prefer the renderer observed to succeed on recent live customer orders.
    for (const method of ['data-url', 'blob']) {
      try {
        if (captureRemaining(state) < 1200) throw captureError('capture-budget-exhausted');
        state.currentStage = method + '-start';
        const canvas = await rasterizeSVG(svg, width, height, method, state);
        if (!canvasHasVisualContent(canvas)) throw captureError(method + '-blank-render');
        captureRemaining(state);
        state.currentStage = method + '-png-export';
        const blob = await new Promise((resolve, reject) =>
          canvas.toBlob(b => b ? resolve(b) : reject(captureError(method + '-png-export')), 'image/png')
        );
        captureRemaining(state);
        if (blob.size > 10000000) throw captureError(method + '-png-too-large');
        return {
          blob, width: canvas.width, height: canvas.height, renderer: method,
          renderTrace: failed.length ? failed.join('|') : 'primary-ok'
        };
      } catch (error) {
        const stage = error.pbStage || method + '-render-unknown';
        failed.push(stage);
        state.history.push(stage);
        if (state.signal.aborted) throw captureError('capture-cancelled', error);
      }
    }
    const failure = captureError('both-renderers-failed');
    failure.pbTrace = failed.join('|');
    throw failure;
  }

  async function capture(node) {
    const controller = new AbortController();
    const state = {
      signal: controller.signal,
      deadline: performance.now() + 18000,
      currentStage: 'capture-start',
      history: []
    };
    const started = performance.now();
    const runner = (async () => {
      const attempts = [];
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const result = await captureOnce(node, state);
          return { ...result, elapsedMs: Math.round(performance.now() - started) };
        } catch (error) {
          const stage = error.pbTrace || error.pbStage || state.currentStage;
          attempts.push(stage);
          if (state.signal.aborted || captureRemaining(state) < 4800 || attempt === 1) break;
          await wait(220);
          await frames();
        }
      }
      const error = captureError('capture-exhausted');
      error.pbTrace = attempts.join(';').slice(0, 220);
      throw error;
    })();
    try {
      return await limited(runner, 18000);
    } catch (error) {
      // Stops in-flight SVG decoding where possible. Resource fetches are checked before reuse.
      controller.abort();
      if (error.pbStage) throw error;
      const timeout = captureError('capture-timeout', error);
      timeout.pbTrace = ('capture-timeout|' + state.currentStage + '|' + state.history.slice(-2).join('|')).slice(0, 240);
      throw timeout;
    }
  }

    function requestForm(init) {
    if (init.body instanceof FormData) {
      const form = new FormData();
      for (const [key, value] of init.body) form.append(key, value);
      return form;
    }
    const payload = JSON.parse(init.body);
    const form = new FormData();
    const append = (value, name) => {
      if (value === undefined || value === null) return;
      if (typeof value === 'object') for (const [key, child] of Object.entries(value)) append(child, `${name}[${key}]`);
      else form.append(name, String(value));
    };
    for (const [key, value] of Object.entries(payload)) {
      if (key === 'sections' && Array.isArray(value)) form.append(key, value.join(','));
      else append(value, key);
    }
    return form;
  }
  function linePrefixes(form) {
    if (form.has('id')) return ['properties'];
    return [...form.keys()].filter(key => /^items\[\d+\]\[id\]$/.test(key)).map(key => key.replace(/\[id\]$/, '[properties]'))
      .filter(prefix => !form.has(`${prefix}[_PB_Internal_Fee]`));
  }
  function findRoot(form) {
    const section = form?.closest('product-info, .shopify-section');
    return section?.querySelector('[data-pb-wedding-inline]') || null;
  }
  function ownerFor(root, index) {
    if (root.id.startsWith('PBStandaloneUSBCustomizer-')) return index === 0 ? 'usb' : 'addon';
    if (root.id.startsWith('PBUSBBoxCustomizer-')) return index === 0 ? 'box' : 'usb';
    return 'box';
  }
  function chooseOwner(root, owner) {
    window.PBCustomizerRepairV1?.preview?.(root, owner);
    window.PBCustomizerVisualRepairV2?.preview?.(root, owner);
    window.PBLogoV3Fixes?.syncAll?.();
  }
  function notice(message, role = 'status') {
    const node = document.createElement('div');
    node.setAttribute('role', role);
    node.textContent = message;
    node.style.cssText = 'position:fixed;inset:0;z-index:2147483647;background:rgba(255,255,255,.94);display:flex;align-items:center;justify-content:center;padding:30px;text-align:center;font:18px/1.5 sans-serif;color:#262626';
    document.body.appendChild(node);
    return node;
  }
  function showError(message) {
    document.getElementById('pb-snapshot-error')?.remove();
    const el = notice(message, 'alert');
    el.id = 'pb-snapshot-error';
    el.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);z-index:2147483647;background:#fff8f6;border:1px solid #a33;padding:18px;max-width:90vw;width:480px;font:15px/1.5 sans-serif;color:#552222';
    const close = document.createElement('button');
    close.type = 'button'; close.textContent = strings.dismiss || 'Dismiss';
    close.style.cssText = 'display:block;margin-top:8px';
    close.addEventListener('click', () => el.remove()); el.appendChild(close);
  }
  async function weddingReference(root) {
    const section = root.closest('product-info, .shopify-section');
    const selected = root.querySelector('[data-pb-design-button][aria-pressed="true"] .pb-wedding-inline__design-image-wrap');
    const gallery = section?.querySelector('.product__media-item.is-active .product__media') || section?.querySelector('.product__media');
    if (!gallery) throw fail();
    const result = await capture(gallery);
    if (selected && visible(selected)) result.reference = await capture(selected);
    return result;
  }
  async function add(root, url, init) {
    if (!root) return fetch(url, init);
    if (busy) throw new Error(strings.busy || 'Your design is already being saved.');

    busy = true;
    const scope = root.querySelector('.pb-photo-customizer__modal, .pb-usb-customizer__modal, .pb-standalone-usb-customizer__modal') || root;
    const originalOwner = scope.dataset.pbPreviewOwner || ownerFor(root, 0);
    const wasInert = scope.inert;
    const viewport = `${innerWidth}x${innerHeight}`;
    let overlay = null;
    const block = event => {
      if (!overlay || !overlay.contains(event.target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    for (const event of ['click', 'input', 'change', 'keydown', 'submit', 'pointerdown']) {
      document.addEventListener(event, block, true);
    }
    // Block edits during capture, without visually changing the preview.

    try {
      try {
        if (root.pbDesignPending?.size) {
          await limited(Promise.all([...root.pbDesignPending]));
        }
      } catch (error) {
        console.warn('PB preview assets did not fully settle; continuing cart add.', error);
      }

      let form;
      try {
        form = requestForm(init);
      } catch (error) {
        console.warn('PB preview form conversion failed; continuing cart add without snapshot.', error);
        return fetch(url, init);
      }

      const prefixes = linePrefixes(form);
      if (!prefixes.length) {
        console.warn('PB preview could not identify cart line properties; continuing cart add without snapshot.');
        return fetch(url, init);
      }

      if (root.dataset.pbKcCuratedProperties) {
        try {
          const curated = JSON.parse(root.dataset.pbKcCuratedProperties);
          for (const prefix of prefixes) {
            for (const [key, value] of Object.entries(curated)) {
              if (value !== undefined && value !== null) {
                form.set(`${prefix}[${key}]`, String(value));
              }
            }
          }
        } catch (error) {
          console.warn('PB curated properties could not be copied; continuing cart add.', error);
        }
      }

      const reference = root.matches('[data-pb-wedding-inline]');
      const photo = root.matches('.pb-photo-customizer');
      // Capture the whole Keepsake display, not its cover alone. Book and Standard
      // spine artwork may extend beyond the cover's bounding box.
      const preview = root.querySelector('[data-pb-kc-preview], .pb-photo-customizer__preview-wrap');
      const designID = crypto.randomUUID();
      const capturedAt = new Date().toISOString();

      for (const [index, prefix] of prefixes.entries()) {
        form.set(`${prefix}[_PB_Design_ID]`, `${designID}-${index + 1}`);
        form.set(`${prefix}[_PB_Design_Captured_At]`, capturedAt);
        form.set(`${prefix}[_PB_Design_Viewport]`, viewport);
        form.set(`${prefix}[_PB_Design_Version]`, VERSION);

        try {
          if (photo) {
            chooseOwner(root, ownerFor(root, index));
            await wait(320);
          }

          for (let attempt = 0; preview?.querySelector('[data-pb-logo-fix-busy]'); attempt++) {
            if (attempt > 100) throw new Error('PB preview remained busy.');
            await wait(50);
          }

          const result = reference ? await weddingReference(root) : await capture(preview);
          if (`${innerWidth}x${innerHeight}` !== viewport) {
            throw new Error('PB viewport changed during preview capture.');
          }

          const placeholder = photo && [...preview.querySelectorAll('.pb-logo-v3__placeholder')].some(visible);
          const kind = reference
            ? 'Design reference; production mock-up pending'
            : placeholder
              ? 'Artwork uploaded; logo visual preview unavailable'
              : 'Customizer preview';

          form.set(
            `${prefix}[Design preview]`,
            new File([result.blob], `PB-${designID}-${index + 1}.png`, { type: 'image/png' })
          );

          if (result.reference) {
            form.set(
              `${prefix}[Design reference]`,
              new File([result.reference.blob], `PB-${designID}-${index + 1}-reference.png`, { type: 'image/png' })
            );
          }

          form.set(`${prefix}[_PB_Design_Renderer]`, result.renderer || 'unknown');
          form.set(`${prefix}[_PB_Design_Render_Trace]`, result.renderTrace || 'primary-ok');
          form.set(`${prefix}[_PB_Design_Capture_MS]`, String(result.elapsedMs || 0));
          form.set(`${prefix}[_PB_Design_Preview_Kind]`, kind);
          form.set(`${prefix}[_PB_Design_Preview_Status]`, 'saved');
          if (reference || placeholder) form.set(`${prefix}[Preview note]`, kind);
        } catch (error) {
          console.warn('PB preview capture unavailable for cart line; continuing purchase without image.', {
            line: index + 1,
            error
          });
          const reason = String(error.pbTrace || error.pbStage || 'capture-unclassified')
            .replace(/[^a-z0-9;|_-]/gi, '-').slice(0, 240);
          form.set(`${prefix}[_PB_Design_Preview_Error]`, reason);
          form.set(`${prefix}[_PB_Design_Preview_Status]`, 'unavailable');
          form.set(`${prefix}[_PB_Design_Preview_Kind]`, 'Preview capture unavailable; personalization properties retained');
        }
      }

      overlay = notice(strings.saving || 'Saving your design…');
      scope.inert = true;
      const headers = new Headers(init.headers);
      headers.delete('Content-Type');
      headers.set('Accept', 'application/json');
      const response = await fetch(url, { ...init, headers, body: form });
      if (response.ok && [...form.keys()].some(key => key.endsWith('[Design preview]'))) {
        try {
          const confirmation = await limited(response.clone().json(), 2500);
          const items = Array.isArray(confirmation?.items) ? confirmation.items : [confirmation];
          const attempted = [...form.keys()].filter(key => key.endsWith('[Design preview]')).length;
          const confirmed = items.filter(item => {
            const value = item?.properties?.['Design preview'];
            return typeof value === 'string' && value.startsWith('https://cdn.shopify.com/');
          }).length;
          if (confirmed < attempted) console.warn('PB preview upload URL not fully confirmed in cart response.', { attempted, confirmed });
        } catch (error) {
          console.warn('PB preview upload acknowledgment could not be inspected.', error);
        }
      }
      return response;
    } finally {
      if (root.matches('.pb-photo-customizer')) chooseOwner(root, originalOwner);
      scope.inert = wasInert;
      if (overlay) overlay.remove();
      for (const event of ['click', 'input', 'change', 'keydown', 'submit', 'pointerdown']) {
        document.removeEventListener(event, block, true);
      }
      busy = false;
    }
  }

  window.PBDesignSnapshot = { version: VERSION, add, capture, findRoot };
})();
